/**
 * Performance optimization utilities for the job platform
 */

// Image optimization
export const imageOptimization = {
  // Lazy load images
  lazyLoadImage: (src: string, alt: string = '') => {
    const img = new Image();
    img.src = src;
    img.alt = alt;
    img.loading = 'lazy';
    return img;
  },

  // Create responsive image sources
  createResponsiveImage: (baseSrc: string, sizes: number[]) => {
    return sizes.map(size => ({
      src: `${baseSrc}?w=${size}`,
      width: size,
    }));
  },

  // Optimize SVG icons
  optimizeSVG: (svgString: string) => {
    return svgString
      .replace(/\s+/g, ' ')
      .replace(/<!--.*?-->/g, '')
      .trim();
  },
};

// API request optimization
export const apiOptimization = {
  // Debounce API calls
  debounce: <T extends (...args: never[]) => unknown>(
    func: T,
    delay: number
  ): ((...args: Parameters<T>) => void) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  },

  // Throttle API calls
  throttle: <T extends (...args: never[]) => unknown>(
    func: T,
    delay: number
  ): ((...args: Parameters<T>) => void) => {
    let lastCall = 0;
    return (...args: Parameters<T>) => {
      const now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        func(...args);
      }
    };
  },

  // Request deduplication
  requestDeduplication: (() => {
    const cache = new Map<string, Promise<unknown>>();
    
    return <T>(key: string, requestFn: () => Promise<T>): Promise<T> => {
      if (cache.has(key)) {
        return cache.get(key) as Promise<T>;
      }
      
      const promise = requestFn().finally(() => {
        cache.delete(key);
      });
      
      cache.set(key, promise);
      return promise;
    };
  })(),
};

// Memory optimization
export const memoryOptimization = {
  // Clean up event listeners
  cleanupEventListeners: (element: HTMLElement, events: string[]) => {
    events.forEach(event => {
      element.removeEventListener(event, () => {});
    });
  },

  // Optimize component re-renders
  shouldComponentUpdate: <T extends Record<string, unknown>>(prevProps: T, nextProps: T) => {
    return JSON.stringify(prevProps) !== JSON.stringify(nextProps);
  },

  // Memory usage tracking
  trackMemoryUsage: (componentName: string) => {
    if ('memory' in performance && performance.memory) {
      const memory = performance.memory;
      console.log(`${componentName} memory usage:`, {
        used: Math.round(memory.usedJSHeapSize / 1024 / 1024) + 'MB',
        total: Math.round(memory.totalJSHeapSize / 1024 / 1024) + 'MB',
      });
    }
  },
};

// Bundle optimization
export const bundleOptimization = {
  // Dynamic imports with retry logic
  dynamicImport: async <T = unknown>(importFn: () => Promise<T>, retries = 3): Promise<T> => {
    for (let i = 0; i < retries; i++) {
      try {
        return await importFn();
      } catch (error) {
        if (i === retries - 1) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
      }
    }
    throw new Error('Dynamic import failed after retries');
  },

  // Preload critical resources
  preloadCriticalResources: () => {
    const resources = [
      { href: '/src/index.css', as: 'style' },
      { href: '/src/main.tsx', as: 'script' },
    ];

    resources.forEach(resource => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource.href;
      link.as = resource.as;
      document.head.appendChild(link);
    });
  },

  // Service worker for caching — disabled until sw.js is implemented
  registerServiceWorker: async () => {
    // No-op: registering a non-existent sw.js causes a console error that
    // drops Lighthouse Best Practices score. Re-enable once a real SW is added.
  },
};

// Performance monitoring
export const performanceTracking = {
  // Track Core Web Vitals
  trackCoreWebVitals: () => {
    if ('web-vitals' in window && window['web-vitals']) {
      const { getCLS, getFID, getFCP, getLCP, getTTFB } = window['web-vitals'];
      
      getCLS(console.log);
      getFID(console.log);
      getFCP(console.log);
      getLCP(console.log);
      getTTFB(console.log);
    }
  },

  // Track component render times
  trackComponentRender: (componentName: string) => {
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      console.log(`${componentName} rendered in ${renderTime.toFixed(2)}ms`);
    };
  },

  // Track route changes
  trackRouteChange: (routeName: string) => {
    const startTime = performance.now();
    
    return () => {
      const endTime = performance.now();
      const changeTime = endTime - startTime;
      console.log(`Route ${routeName} loaded in ${changeTime.toFixed(2)}ms`);
    };
  },
};

// Initialize performance optimizations
export const initializePerformanceOptimizations = () => {
  // Preload critical resources
  bundleOptimization.preloadCriticalResources();
  
  // Register service worker
  bundleOptimization.registerServiceWorker();
  
  // Track core web vitals
  performanceTracking.trackCoreWebVitals();
  
  // Monitor memory usage in development
  if (process.env.NODE_ENV === 'development') {
    setInterval(() => {
      memoryOptimization.trackMemoryUsage('Global');
    }, 30000);
  }
};

// All optimizations are already exported above